import { useState } from 'react';
import styles from './Quiz.module.css';

const QUESTIONS = [
  {
    id: 'experience_time',
    section: 'About You',
    text: 'How long have you been trading futures?',
    options: [
      { label: 'Less than 6 months', value: 'lt6mo' },
      { label: '6 months – 1 year', value: '6mo1yr' },
      { label: '1–2 years', value: '1to2yr' },
      { label: '3+ years', value: '3plus' },
    ],
  },
  {
    id: 'prop_experience',
    section: 'About You',
    text: 'Have you traded with a prop firm before?',
    options: [
      { label: 'No, this would be my first', value: 'never' },
      { label: 'Yes, but never passed an eval', value: 'tried' },
      { label: "Yes, I've been funded before", value: 'funded' },
      { label: "Yes, I've taken payouts", value: 'payouts' },
    ],
  },
  {
    id: 'payout_count',
    section: 'About You',
    text: 'How many total payouts have you taken across all firms?',
    options: [
      { label: "0 — haven't taken any yet", value: 'zero' },
      { label: '1–3 payouts', value: '1to3' },
      { label: '4–10 payouts', value: '4to10' },
      { label: '10+ payouts', value: '10plus' },
    ],
  },
  {
    id: 'challenge',
    section: 'About You',
    text: "What's your biggest challenge right now?",
    options: [
      { label: 'Passing the evaluation', value: 'passing' },
      { label: 'Staying funded without breaching', value: 'staying_funded' },
      { label: 'Taking consistent payouts', value: 'consistent_payouts' },
      { label: 'Maximizing my income across accounts', value: 'maximizing' },
    ],
  },
  {
    id: 'budget',
    section: 'Practical',
    text: 'How much are you looking to spend on an eval?',
    note: 'Some firms charge activation fees on top of the eval — factor in all-in cost.',
    options: [
      { label: 'Under $50 — I want cheap evals even if I need a few tries', value: 'under50' },
      { label: '$50–$150 — I want a good all-in price to get funded', value: '50to150' },
      { label: '$150–$300 — willing to invest for better terms', value: '150to300' },
      { label: "Budget isn't a concern", value: 'any' },
    ],
  },
  {
    id: 'eval_pref',
    section: 'Practical',
    text: 'Would you rather skip the eval or pass one for better funded terms?',
    options: [
      { label: 'Skip it — I want instant funding', value: 'skip' },
      { label: "I'll pass an eval for better rules and payouts", value: 'eval' },
      { label: 'Whichever is cheaper', value: 'cheapest' },
    ],
  },
  {
    id: 'style',
    section: 'Trading Style',
    text: 'What best describes your trading style?',
    options: [
      { label: 'Scalper — quick in and out, many trades per day', value: 'scalper' },
      { label: 'Day trader — a few setups per day, flat by close', value: 'daytrader' },
      { label: 'Swing / position — hold for hours or longer', value: 'swing' },
    ],
  },
  {
    id: 'drawdown_pref',
    section: 'Trading Style',
    text: 'Which drawdown type do you prefer?',
    note: 'EOD trailing recalculates at end of day — more forgiving. Intraday trailing moves in real time with your equity.',
    options: [
      { label: 'EOD trailing — recalculates at end of day', value: 'eod' },
      { label: 'Intraday trailing — I can handle real-time tracking', value: 'intraday' },
      { label: "I don't know the difference", value: 'unsure' },
    ],
  },
  {
    id: 'dll_pref',
    section: 'Trading Style',
    text: 'Do you want a Daily Loss Limit on your funded account?',
    note: 'A DLL caps how much you can lose in a single day. Soft DLLs pause trading — hard DLLs fail your account.',
    options: [
      { label: "No — I just want the trailing drawdown as my only floor", value: 'no_dll' },
      { label: "A soft one is fine — pauses trading but doesn't fail me", value: 'soft_ok' },
      { label: "Don't care either way", value: 'dont_care' },
    ],
  },
  {
    id: 'consistency_pref',
    section: 'Trading Style',
    text: 'How do you feel about funded consistency rules?',
    note: 'Consistency rules cap how much of your total profit can come from a single day.',
    options: [
      { label: 'No consistency — I want to size heavy early and cycle winning days', value: 'none' },
      { label: "A loose rule is fine (35–50%) — doesn't really affect my trading", value: 'loose_ok' },
      { label: "I don't mind a strict one if the other terms are good", value: 'strict_ok' },
    ],
  },
  {
    id: 'scaling_style',
    section: 'Account Preferences',
    text: 'How many funded accounts do you plan to run?',
    options: [
      { label: 'One or two — keep it simple', value: '1to2' },
      { label: '3–5 accounts', value: '3to5' },
      { label: 'As many as possible — I want to scale aggressively', value: 'max' },
    ],
  },
  {
    id: 'platform',
    section: 'Platform',
    text: 'Do you have a preferred trading platform?',
    options: [
      { label: 'NinjaTrader', value: 'ninjatrader' },
      { label: 'Tradovate', value: 'tradovate' },
      { label: 'TradingView', value: 'tradingview' },
      { label: 'Quantower / ProjectX', value: 'quantower' },
      { label: 'Rithmic / TradeSea', value: 'rithmic' },
      { label: 'No preference', value: 'any' },
    ],
  },
];

const ACCOUNT_TYPES = {
  ts_standard: {
    id: 'ts_standard', firmId: 'Topstep', firmName: 'Topstep', productName: 'XFA Standard',
    baseScore: 4,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$5,000',
    payoutSpeed: 'moderate', maxAccounts: 5, activationFee: true,
    platforms: ['TopstepX'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'NFA regulated — the only regulated futures prop firm',
      'No daily loss limit on funded accounts',
      'No funded consistency rule — trade your best days freely',
      'EOD trailing drawdown — intraday dips don\'t count',
      'Back2Funded: reactivate up to 2 times if you breach',
    ],
  },
  ts_consistency: {
    id: 'ts_consistency', firmId: 'Topstep', firmName: 'Topstep', productName: 'XFA Consistency',
    baseScore: 3,
    drawdown: 'eod', fundedConsistency: '40%', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$6,000',
    payoutSpeed: 'moderate', maxAccounts: 5, activationFee: true,
    platforms: ['TopstepX'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'NFA regulated — the only regulated futures prop firm',
      'No daily loss limit on funded accounts',
      'Higher $6,000 payout cap than Standard path',
      'EOD trailing drawdown — intraday dips don\'t count',
      '40% consistency rule with only 3 qualifying days needed',
    ],
  },
  apex_eod: {
    id: 'apex_eod', firmId: 'apex', firmName: 'Apex Trader Funding', productName: 'EOD Account',
    baseScore: 4,
    drawdown: 'eod', fundedConsistency: '50%', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '100%', maxPayout: '$5,000 (150K step 6)',
    payoutSpeed: 'moderate', maxAccounts: 20, activationFee: true,
    platforms: ['NinjaTrader', 'Tradovate', 'Rithmic', 'WealthCharts'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '100% profit split — keep every dollar you earn',
      'Up to 20 funded accounts simultaneously',
      'Cheapest evals in the industry with promos (~$40)',
      'Choose EOD drawdown for end-of-day protection',
      'One-time eval fee — no monthly subscription',
    ],
  },
  apex_intraday: {
    id: 'apex_intraday', firmId: 'apex', firmName: 'Apex Trader Funding', productName: 'Intraday Trail Account',
    baseScore: 3,
    drawdown: 'intraday', fundedConsistency: '50%', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '100%', maxPayout: '$2,500 (150K step 6)',
    payoutSpeed: 'moderate', maxAccounts: 20, activationFee: true,
    platforms: ['NinjaTrader', 'Tradovate', 'Rithmic', 'WealthCharts'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '100% profit split — keep every dollar you earn',
      'Up to 20 funded accounts simultaneously',
      'Cheapest evals with promos (~$24 for intraday)',
      'Lower activation fee ($79 vs $99 for EOD)',
      'One-time eval fee — no monthly subscription',
    ],
  },
  tradeify_select: {
    id: 'tradeify_select', firmId: 'tradeify', firmName: 'Tradeify', productName: 'Select',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: 'none (Flex) / varies (Daily)', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$25,000 (Growth path)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'Rithmic'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'Choose Flex or Daily funded path after passing one eval',
      'No activation fee — low all-in cost to get funded',
      'Route to Tradeify Elite for live CME capital',
      'Growth path allows up to $25,000 per payout',
      'No funded consistency rule on Flex path',
    ],
  },
  tradeify_lightning: {
    id: 'tradeify_lightning', firmId: 'tradeify', firmName: 'Tradeify', productName: 'Lightning',
    baseScore: 3,
    drawdown: 'eod', fundedConsistency: '20→25→30% progressive', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$25,000 (payout 6+)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'Rithmic'],
    isInstantFunded: true, hasEval: false,
    strengths: [
      'Instant funded — no evaluation required',
      'Route to Tradeify Elite for live CME capital',
      'EOD trailing drawdown locks after first payout',
      '90% profit split from day one',
      'Payout caps scale up to $25,000 from payout 6+',
    ],
  },
  alpha_standard: {
    id: 'alpha_standard', firmId: 'alpha', firmName: 'Alpha Futures', productName: 'Standard',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: '40%', hasDLL: true, dllType: 'soft (DLG)',
    newsRestricted: true, newsNote: 'No new orders within 2 min of high-impact events on qualified accounts.',
    overnightAllowed: true, profitSplit: '70→80→90%', maxPayout: '$15,000',
    payoutSpeed: 'fast', maxAccounts: 3, activationFee: true,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'CQG'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'Only prop firm that allows overnight holds',
      'DLG protects from blowups without failing the account',
      'Profit split scales 70→80→90% over payouts',
      '$15,000 max payout per request',
      'Alpha Prime: path to live capital after 5 payouts or $40K profit',
    ],
  },
  alpha_advanced: {
    id: 'alpha_advanced', firmId: 'alpha', firmName: 'Alpha Futures', productName: 'Advanced',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: true, profitSplit: '90%', maxPayout: '$15,000',
    payoutSpeed: 'fast', maxAccounts: 3, activationFee: true,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'CQG'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '90% flat profit split from payout 1 — no scaling tiers',
      'No Daily Loss Limit — maximum funded freedom',
      'No funded consistency rule — trade your best days freely',
      '$15,000 max payout per request',
      'Only prop firm that allows overnight holds',
    ],
  },
  alpha_zero: {
    id: 'alpha_zero', firmId: 'alpha', firmName: 'Alpha Futures', productName: 'Zero',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: '40%', hasDLL: true, dllType: 'soft (DLG)',
    newsRestricted: false, newsNote: null,
    overnightAllowed: true, profitSplit: '90%', maxPayout: '$2,500 (100K)',
    payoutSpeed: 'fast', maxAccounts: 3, activationFee: false,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'CQG'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '$0 activation fee — lowest all-in cost after passing',
      '90% flat profit split from day one',
      'DLG on all phases — built-in protection while you learn',
      'No eval consistency rule — can pass in a single day',
      'Great stepping stone before upgrading to Advanced',
    ],
  },
  mffu_flex: {
    id: 'mffu_flex', firmId: 'mffu', firmName: 'MyFundedFutures', productName: 'Flex',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '80%', maxPayout: '$5,000 (50K)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'ATAS', 'Volumetrica'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'No funded consistency rule — size heavy and cycle winning days',
      'No Daily Loss Limit — trailing drawdown is your only floor',
      'EOD trailing locks permanently after first payout',
      '$0 activation fee — cheapest all-in path to funded',
      '4.9 Trustpilot — highest rated in the industry',
    ],
  },
  mffu_pro: {
    id: 'mffu_pro', firmId: 'mffu', firmName: 'MyFundedFutures', productName: 'Pro',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: true, newsNote: 'T1 news events (FOMC, CPI, NFP, EIA, Ag Reports) blocked on funded — no positions 2 min before/after.',
    overnightAllowed: false, profitSplit: '80%', maxPayout: 'No cap',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'ATAS', 'Volumetrica'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'No per-payout cap — withdraw any amount above the buffer',
      'No funded consistency rule',
      'No Daily Loss Limit',
      'Live account trigger after 3 consecutive payouts',
      '$0 activation fee',
    ],
  },
  mffu_rapid: {
    id: 'mffu_rapid', firmId: 'mffu', firmName: 'MyFundedFutures', productName: 'Rapid',
    baseScore: 3,
    drawdown: 'intraday', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: true, newsNote: 'T1 news events blocked on funded — no positions 2 min before/after.',
    overnightAllowed: false, profitSplit: '90%', maxPayout: 'No cap',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'ATAS', 'Volumetrica'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '90% profit split — highest MFFU split',
      'No per-payout cap — unlimited withdrawal potential',
      'Daily payouts — withdraw every day',
      'No funded consistency rule',
      '$10,000 daily profit cap on sim funded',
    ],
  },
  tpt_pro: {
    id: 'tpt_pro', firmId: 'tpt', firmName: 'Take Profit Trader', productName: 'PRO',
    baseScore: 3,
    drawdown: 'intraday', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: true, newsNote: 'No positions 1 min before/after FOMC, NFP, CPI. Instrument-specific restrictions on CL and bonds.',
    overnightAllowed: false, profitSplit: '80%', maxPayout: 'No cap',
    payoutSpeed: 'moderate', maxAccounts: 5, activationFee: true,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'Quantower', 'MotiveWave', 'Bookmap', 'Sierra Chart'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'No per-payout cap — withdraw any amount above the buffer',
      'Path to PRO+ with real CME live capital',
      'No funded consistency rule',
      'No Daily Loss Limit',
      '15+ supported trading platforms',
    ],
  },
  fn_bolt: {
    id: 'fn_bolt', firmId: 'fundednext', firmName: 'FundedNext Futures', productName: 'Bolt',
    baseScore: 3,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '80%', maxPayout: '$1,200 (tiered)',
    payoutSpeed: 'fast', maxAccounts: 10, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '24-hour payout guarantee — $1,000 compensation if missed',
      'No funded consistency rule',
      'No activation fee',
      'News trading fully allowed',
      '$50K only — focused single-size product',
    ],
  },
  fn_rapid: {
    id: 'fn_rapid', firmId: 'fundednext', firmName: 'FundedNext Futures', productName: 'Rapid',
    baseScore: 3,
    drawdown: 'eod', fundedConsistency: '40%', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '80%', maxPayout: '$2,500 (100K)',
    payoutSpeed: 'fast', maxAccounts: 10, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '24-hour payout guarantee — $1,000 compensation if missed',
      'No eval consistency rule — can pass in one day',
      'No activation fee',
      'Payout every 3 days',
      'After 5 payouts, all caps removed — unlimited withdrawals',
    ],
  },
  fn_legacy: {
    id: 'fn_legacy', firmId: 'fundednext', firmName: 'FundedNext Futures', productName: 'Legacy',
    baseScore: 3,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '80%', maxPayout: '$6,000 (50K/100K after 30 days)',
    payoutSpeed: 'fast', maxAccounts: 10, activationFee: false,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '24-hour payout guarantee — $1,000 compensation if missed',
      'No funded consistency rule',
      'After 30 benchmark days all caps removed — 100% of profit',
      'No activation fee',
      'News trading fully allowed',
    ],
  },
  lucid_flex: {
    id: 'lucid_flex', firmId: 'lucid', firmName: 'Lucid Trading', productName: 'LucidFlex',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$3,000 (150K)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'Quantower', 'Sierra Chart', 'MotiveWave', 'Bookmap', 'ATAS', 'Jigsaw', 'MultiCharts'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'No funded consistency rule',
      'No Daily Loss Limit — trailing drawdown only',
      'Most platform options of any prop firm (11+)',
      '90% profit split from payout 1',
      'LucidLive: live capital after 5 payouts with daily payouts',
    ],
  },
  lucid_pro: {
    id: 'lucid_pro', firmId: 'lucid', firmName: 'Lucid Trading', productName: 'LucidPro',
    baseScore: 5,
    drawdown: 'eod', fundedConsistency: '40%', hasDLL: true, dllType: 'soft (fixed + scaling)',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$3,000 (150K)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'Quantower', 'Sierra Chart', 'MotiveWave', 'Bookmap', 'ATAS', 'Jigsaw', 'MultiCharts'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '3-day payout cycles — request every 3 calendar days',
      'No eval consistency rule — can pass in one day',
      '90% profit split from payout 1',
      'Most platform options of any prop firm (11+)',
      'LucidLive: live capital after 5 payouts',
    ],
  },
  lucid_direct: {
    id: 'lucid_direct', firmId: 'lucid', firmName: 'Lucid Trading', productName: 'LucidDirect',
    baseScore: 2,
    drawdown: 'eod', fundedConsistency: '20%', hasDLL: true, dllType: 'soft (fixed + scaling)',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$3,000 (150K)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'Quantower', 'Sierra Chart', 'MotiveWave', 'Bookmap', 'ATAS', 'Jigsaw', 'MultiCharts'],
    isInstantFunded: true, hasEval: false,
    strengths: [
      'Instant funded — no evaluation required',
      '90% profit split from payout 1',
      'Most platform options of any prop firm (11+)',
      'LucidLive: live capital after 6 payouts',
      'Larger drawdown than LucidFlex/Pro on 100K and 150K',
    ],
  },
  to_elite: {
    id: 'to_elite', firmId: 'topone', firmName: 'Top One Futures', productName: 'Elite',
    baseScore: 2,
    drawdown: 'eod', fundedConsistency: '25%', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '100% first $20K / 90% after', maxPayout: '$3,500 (150K)',
    payoutSpeed: 'fast', maxAccounts: 3, activationFee: true,
    platforms: ['NinjaTrader', 'TradingView', 'Tradovate', 'Quantower', 'Rithmic', 'ProjectX'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      '100% profit split on first $20K withdrawn',
      'Under 4-hour payout processing',
      'Cheapest evals on sale (~$41 for 25K)',
      'Path to live account after 3 payouts',
      'News trading fully allowed',
    ],
  },
  to_daily: {
    id: 'to_daily', firmId: 'topone', firmName: 'Top One Futures', productName: 'Elite Daily',
    baseScore: 2,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: true, newsNote: 'News restricted on funded — no trades 2 min before/after high-impact releases.',
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$1,500 (100K)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['NinjaTrader', 'TradingView', 'Tradovate', 'Quantower', 'Rithmic', 'ProjectX'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'Daily payouts — eligible from day 1 of funded',
      'No funded consistency rule',
      'No Daily Loss Limit',
      'No activation fee',
      '90% profit split',
    ],
  },
  to_instant: {
    id: 'to_instant', firmId: 'topone', firmName: 'Top One Futures', productName: 'Instant Sim Funded',
    baseScore: 2,
    drawdown: 'eod', fundedConsistency: '20%', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '100% first $20K / 90% after', maxPayout: '$3,500 (150K)',
    payoutSpeed: 'fast', maxAccounts: 5, activationFee: false,
    platforms: ['NinjaTrader', 'TradingView', 'Tradovate', 'Quantower', 'Rithmic', 'ProjectX'],
    isInstantFunded: true, hasEval: false,
    strengths: [
      'Instant funded — no evaluation required',
      '100% profit split on first $20K withdrawn',
      'Under 4-hour payout processing',
      'No contract scaling — full contracts from day one',
      'Path to live account after 3 payouts',
    ],
  },
  to_s2f: {
    id: 'to_s2f', firmId: 'topone', firmName: 'Top One Futures', productName: 'S2F Sim PRO',
    baseScore: 1,
    drawdown: 'intraday', fundedConsistency: '20% ESS', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '100% first $20K / 90% after', maxPayout: '$3,500 (150K)',
    payoutSpeed: 'fast', maxAccounts: 10, activationFee: false,
    platforms: ['NinjaTrader', 'TradingView', 'Tradovate', 'Quantower', 'Rithmic', 'ProjectX'],
    isInstantFunded: true, hasEval: false,
    strengths: [
      'Instant funded — no evaluation required',
      '100% profit split on first $20K withdrawn',
      'Fastest path to live — reviewed after 2 payouts',
      'Copy trading allowed (PRO→PRO, same size)',
      'Up to 10 accounts simultaneously',
    ],
  },
  to_ignite: {
    id: 'to_ignite', firmId: 'topone', firmName: 'Top One Futures', productName: 'Ignite',
    baseScore: 1,
    drawdown: 'eod', fundedConsistency: '15%', hasDLL: true, dllType: 'soft',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '90%', maxPayout: '$2,000 (150K)',
    payoutSpeed: 'fast', maxAccounts: 10, activationFee: false,
    platforms: ['NinjaTrader', 'TradingView', 'Tradovate', 'Quantower', 'Rithmic', 'ProjectX'],
    isInstantFunded: true, hasEval: false,
    strengths: [
      'Budget instant funded — cheapest instant option',
      'EOD trailing drawdown — more forgiving than S2F PRO',
      '90% profit split',
      'Up to 10 accounts simultaneously',
      'Path to live account after 3 payouts',
    ],
  },
  td_eod: {
    id: 'td_eod', firmId: 'tradeday', firmName: 'TradeDay', productName: 'EOD Account',
    baseScore: 3,
    drawdown: 'eod', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: true, newsNote: 'News trading prohibited — auto-liquidated 2 min before/after T1 data releases.',
    overnightAllowed: false, profitSplit: '80→90→95%', maxPayout: 'No cap',
    payoutSpeed: 'fast', maxAccounts: 3, activationFee: true,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'Jigsaw'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'Day-one payouts once funded buffer is cleared',
      'No funded consistency rule',
      'No Daily Loss Limit',
      'Profit split scales from 80% up to 95% lifetime',
      'No payout cap — withdraw any amount',
    ],
  },
  td_intraday: {
    id: 'td_intraday', firmId: 'tradeday', firmName: 'TradeDay', productName: 'Intraday Account',
    baseScore: 2,
    drawdown: 'intraday', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: true, newsNote: 'News trading prohibited — auto-liquidated 2 min before/after T1 data releases.',
    overnightAllowed: false, profitSplit: '80→90→95%', maxPayout: 'No cap',
    payoutSpeed: 'fast', maxAccounts: 3, activationFee: true,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'Jigsaw'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'Cheapest TradeDay option — lowest monthly fee',
      'Day-one payouts once funded buffer is cleared',
      'No funded consistency rule',
      'No Daily Loss Limit',
      'Profit split scales from 80% up to 95% lifetime',
    ],
  },
  td_static: {
    id: 'td_static', firmId: 'tradeday', firmName: 'TradeDay', productName: 'Static Account',
    baseScore: 1,
    drawdown: 'static', fundedConsistency: 'none', hasDLL: false, dllType: null,
    newsRestricted: true, newsNote: 'News trading prohibited — auto-liquidated 2 min before/after T1 data releases.',
    overnightAllowed: false, profitSplit: '80→90→95%', maxPayout: 'No cap',
    payoutSpeed: 'fast', maxAccounts: 3, activationFee: true,
    platforms: ['Tradovate', 'NinjaTrader', 'TradingView', 'Quantower', 'Jigsaw'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'Only firm with true static drawdown — floor never moves',
      'Day-one payouts once funded buffer is cleared',
      'No funded consistency rule',
      'Profit split scales from 80% up to 95% lifetime',
      'Lowest profit targets of any TradeDay product',
    ],
  },
  e2t_mini: {
    id: 'e2t_mini', firmId: 'earn2trade', firmName: 'Earn2Trade', productName: 'Gauntlet Mini',
    baseScore: 1,
    drawdown: 'eod', fundedConsistency: 'none (LiveSim)', hasDLL: true, dllType: 'hard',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '80%', maxPayout: '$4,000 (LiveSim cap)',
    payoutSpeed: 'moderate', maxAccounts: 3, activationFee: true,
    platforms: ['NinjaTrader', 'Tradovate', 'TradingView', 'Quantower', 'Sierra Chart', 'MotiveWave', 'Bookmap', 'MultiCharts'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'Oldest futures prop firm — founded in 2016',
      'Education-focused platform with structured training',
      'Free monthly resets included with subscription',
      'Account growth plan from $25K to $400K on live',
      'News trading fully allowed',
    ],
  },
  e8_sig: {
    id: 'e8_sig', firmId: 'e8futures', firmName: 'E8 Futures', productName: 'E8 Signature Futures',
    baseScore: 1,
    drawdown: 'eod', fundedConsistency: '35%', hasDLL: true, dllType: 'soft (Daily Pause)',
    newsRestricted: false, newsNote: null,
    overnightAllowed: false, profitSplit: '80%', maxPayout: '$5,250 (150K step 5+)',
    payoutSpeed: 'moderate', maxAccounts: 5, activationFee: false,
    platforms: ['Tradovate', 'TradingView'],
    isInstantFunded: false, hasEval: true,
    strengths: [
      'EOD Dynamic drawdown — intraday dips that recover don\'t count',
      'No eval consistency rule — can pass in one day',
      'On-demand payouts — request anytime requirements are met',
      'News trading fully allowed on eval and funded',
      'No activation fee',
    ],
  },
};

function scoreResults(answers) {
  const ids = Object.keys(ACCOUNT_TYPES);
  const scores = {};
  ids.forEach(id => { scores[id] = ACCOUNT_TYPES[id].baseScore; });

  // Q1: experience_time
  const expTime = answers.experience_time;
  if (expTime === 'lt6mo') {
    scores.ts_standard += 3; scores.apex_eod += 3; scores.alpha_zero += 4;
    scores.mffu_flex += 3; scores.tradeify_select += 2; scores.lucid_flex += 3;
    scores.e2t_mini += 1;
    scores.alpha_advanced -= 2; scores.mffu_pro -= 2; scores.tpt_pro -= 3;
    scores.mffu_rapid -= 3; scores.to_s2f -= 2; scores.td_intraday -= 2;
    scores.apex_intraday -= 2;
  } else if (expTime === '6mo1yr') {
    scores.mffu_flex += 2; scores.tradeify_select += 2; scores.alpha_zero += 2;
    scores.lucid_flex += 3; scores.apex_eod += 2;
    scores.alpha_standard += 1; scores.ts_standard += 1;
  } else if (expTime === '1to2yr') {
    scores.mffu_flex += 2; scores.alpha_standard += 2; scores.lucid_flex += 2;
    scores.tradeify_select += 2; scores.lucid_pro += 2;
    scores.alpha_advanced += 1; scores.mffu_pro += 1;
  } else if (expTime === '3plus') {
    scores.mffu_pro += 3; scores.alpha_advanced += 3; scores.tpt_pro += 3;
    scores.lucid_pro += 2; scores.tradeify_select += 2; scores.apex_eod += 2;
    scores.mffu_flex += 1; scores.alpha_standard += 1;
  }

  // Q2: prop_experience
  const propExp = answers.prop_experience;
  if (propExp === 'never') {
    scores.ts_standard += 4; scores.apex_eod += 3; scores.alpha_zero += 3;
    scores.tradeify_select += 3; scores.mffu_flex += 3; scores.lucid_flex += 2;
    scores.tpt_pro -= 2; scores.mffu_rapid -= 2; scores.to_s2f -= 2;
  } else if (propExp === 'tried') {
    scores.apex_eod += 4; scores.tradeify_select += 3; scores.alpha_zero += 3;
    scores.mffu_flex += 2; scores.lucid_flex += 2;
    scores.fn_rapid += 1; scores.e8_sig += 1;
  } else if (propExp === 'funded') {
    scores.mffu_flex += 3; scores.lucid_flex += 3; scores.alpha_standard += 3;
    scores.alpha_zero += 3; scores.tradeify_select += 2; scores.ts_standard += 2;
    scores.lucid_pro += 2;
  } else if (propExp === 'payouts') {
    scores.mffu_pro += 4; scores.alpha_advanced += 4; scores.tpt_pro += 4;
    scores.lucid_pro += 3; scores.tradeify_select += 2; scores.apex_eod += 2;
  }

  // Q3: payout_count
  const payouts = answers.payout_count;
  if (payouts === 'zero') {
    scores.apex_eod += 3; scores.alpha_zero += 5; scores.tradeify_select += 3;
    scores.mffu_flex += 3; scores.lucid_flex += 3; scores.ts_standard += 3;
    scores.alpha_advanced -= 2; scores.mffu_pro -= 2; scores.tpt_pro -= 2;
  } else if (payouts === '1to3') {
    scores.mffu_flex += 3; scores.lucid_flex += 3; scores.alpha_standard += 3;
    scores.alpha_zero += 3; scores.tradeify_select += 3; scores.ts_standard += 2;
    scores.lucid_pro += 2;
  } else if (payouts === '4to10') {
    scores.mffu_pro += 4; scores.alpha_advanced += 4; scores.tpt_pro += 4;
    scores.lucid_pro += 3; scores.tradeify_select += 3;
  } else if (payouts === '10plus') {
    scores.apex_eod += 5; scores.mffu_pro += 4; scores.alpha_advanced += 4;
    scores.apex_intraday += 3; scores.to_elite += 2; scores.to_instant += 2;
  }

  // Q4: challenge
  const challenge = answers.challenge;
  if (challenge === 'passing') {
    scores.apex_eod += 4; scores.alpha_zero += 4;
    scores.tradeify_select += 3; scores.lucid_flex += 3; scores.mffu_flex += 3;
    scores.fn_rapid += 2; scores.e8_sig += 2; scores.lucid_pro += 2;
    scores.ts_standard += 1;
  } else if (challenge === 'staying_funded') {
    scores.mffu_flex += 5; scores.lucid_flex += 4; scores.ts_standard += 4;
    scores.alpha_standard += 4; scores.tradeify_select += 3; scores.alpha_zero += 5;
    scores.td_eod += 2;
    scores.mffu_rapid -= 3; scores.apex_intraday -= 3; scores.to_s2f -= 3;
    scores.td_intraday -= 2; scores.tpt_pro -= 2;
  } else if (challenge === 'consistent_payouts') {
    scores.mffu_flex += 4; scores.lucid_flex += 4; scores.alpha_advanced += 4;
    scores.ts_standard += 3; scores.td_eod += 3; scores.tradeify_select += 3;
    scores.to_ignite -= 3; scores.lucid_direct -= 2; scores.to_s2f -= 2;
    scores.to_instant -= 2;
  } else if (challenge === 'maximizing') {
    scores.mffu_pro += 5; scores.alpha_advanced += 4; scores.apex_eod += 4;
    scores.tpt_pro += 4; scores.tradeify_select += 3; scores.apex_intraday += 3;
    scores.lucid_pro += 2;
  }

  // Q5: budget
  const budget = answers.budget;
  if (budget === 'under50') {
    scores.apex_eod += 5; scores.apex_intraday += 4;
    scores.to_elite += 2; scores.mffu_flex += 1;
    scores.ts_standard -= 4; scores.ts_consistency -= 4;
    scores.tpt_pro -= 4; scores.e2t_mini -= 4;
    scores.alpha_advanced -= 3; scores.alpha_standard -= 2;
    scores.td_eod -= 3; scores.td_intraday -= 2; scores.td_static -= 2;
    scores.tradeify_lightning -= 4; scores.lucid_direct -= 4;
    scores.to_instant -= 4; scores.to_s2f -= 3;
  } else if (budget === '50to150') {
    scores.alpha_zero += 4; scores.mffu_flex += 4;
    scores.tradeify_select += 3; scores.lucid_flex += 3;
    scores.lucid_pro += 2; scores.apex_eod += 2;
    scores.e8_sig += 1; scores.fn_bolt += 1; scores.fn_rapid += 1;
    scores.ts_standard -= 2; scores.tpt_pro -= 3; scores.e2t_mini -= 3;
    scores.alpha_advanced -= 1;
  } else if (budget === '150to300') {
    scores.alpha_advanced += 3; scores.mffu_pro += 3;
    scores.mffu_rapid += 2; scores.tpt_pro += 2;
    scores.lucid_pro += 2; scores.alpha_standard += 2;
    scores.ts_standard += 1; scores.tradeify_select += 1;
    scores.tradeify_lightning += 1;
  } else if (budget === 'any') {
    scores.tpt_pro += 3; scores.alpha_advanced += 2; scores.mffu_pro += 2;
    scores.e2t_mini += 2; scores.td_eod += 1;
  }

  // Q6: eval_pref
  const evalPref = answers.eval_pref;
  if (evalPref === 'skip') {
    scores.tradeify_lightning += 7;
    scores.lucid_direct += 4; scores.to_instant += 3;
    scores.to_ignite += 2; scores.to_s2f += 2;
    ids.forEach(id => { if (ACCOUNT_TYPES[id].hasEval) scores[id] -= 2; });
  } else if (evalPref === 'eval') {
    scores.mffu_pro += 3; scores.alpha_advanced += 3;
    scores.mffu_flex += 2; scores.lucid_flex += 2; scores.tradeify_select += 2;
    scores.tradeify_lightning -= 5; scores.lucid_direct -= 5;
    scores.to_instant -= 5; scores.to_ignite -= 5; scores.to_s2f -= 5;
  } else if (evalPref === 'cheapest') {
    scores.apex_eod += 4; scores.apex_intraday += 3;
    scores.tradeify_select += 2; scores.to_elite += 2;
    scores.to_ignite += 1;
  }

  // Q7: style
  const style = answers.style;
  if (style === 'scalper') {
    scores.mffu_flex += 4; scores.lucid_flex += 4; scores.alpha_advanced += 4;
    scores.tradeify_select += 3; scores.ts_standard += 3; scores.td_eod += 2;
    scores.to_ignite -= 4; scores.to_s2f -= 3; scores.lucid_direct -= 3;
    scores.to_instant -= 2; scores.to_elite -= 2;
    scores.e8_sig -= 2; scores.alpha_standard -= 1;
  } else if (style === 'daytrader') {
    scores.ts_standard += 3; scores.mffu_flex += 2; scores.alpha_standard += 2;
    scores.tradeify_select += 2; scores.apex_eod += 2; scores.lucid_flex += 2;
  } else if (style === 'swing') {
    scores.alpha_standard += 7; scores.alpha_advanced += 7; scores.alpha_zero += 5;
    ids.forEach(id => {
      if (!ACCOUNT_TYPES[id].overnightAllowed) scores[id] -= 3;
    });
  }

  // Q8: drawdown_pref
  const drawdown = answers.drawdown_pref;
  if (drawdown === 'eod') {
    scores.ts_standard += 4; scores.ts_consistency += 4;
    scores.mffu_flex += 4; scores.mffu_pro += 4;
    scores.alpha_standard += 3; scores.alpha_advanced += 3; scores.alpha_zero += 3;
    scores.tradeify_select += 3; scores.lucid_flex += 3; scores.lucid_pro += 3;
    scores.fn_bolt += 2; scores.fn_legacy += 2; scores.fn_rapid += 2;
    scores.td_eod += 3; scores.to_elite += 2; scores.to_daily += 2;
    scores.to_instant += 2; scores.to_ignite += 2;
    scores.e2t_mini += 2; scores.e8_sig += 2; scores.apex_eod += 3;
    scores.apex_intraday -= 6; scores.mffu_rapid -= 6; scores.tpt_pro -= 5;
    scores.td_intraday -= 5; scores.to_s2f -= 5;
  } else if (drawdown === 'intraday') {
    scores.apex_intraday += 5; scores.mffu_rapid += 4; scores.tpt_pro += 3;
    scores.to_s2f += 3; scores.td_intraday += 3;
    scores.apex_eod -= 2; scores.td_eod -= 2; scores.ts_standard -= 2;
    ids.forEach(id => {
      const a = ACCOUNT_TYPES[id];
      if (a.drawdown === 'eod' && !['apex_eod', 'td_eod', 'ts_standard'].includes(id)) scores[id] -= 1;
    });
  } else if (drawdown === 'unsure') {
    scores.ts_standard += 4; scores.mffu_flex += 3; scores.tradeify_select += 3;
    scores.alpha_zero += 3; scores.lucid_flex += 2; scores.apex_eod += 2;
    scores.apex_intraday -= 4; scores.mffu_rapid -= 4; scores.tpt_pro -= 3;
    scores.to_s2f -= 3; scores.td_intraday -= 3; scores.td_static -= 3;
  }

  // Q9: dll_pref
  const dll = answers.dll_pref;
  if (dll === 'no_dll') {
    scores.ts_standard += 5; scores.ts_consistency += 5;
    scores.mffu_flex += 5; scores.mffu_pro += 5; scores.mffu_rapid += 4;
    scores.alpha_advanced += 5;
    scores.td_eod += 4; scores.td_intraday += 4; scores.td_static += 4;
    scores.tpt_pro += 4; scores.fn_rapid += 3; scores.fn_legacy += 3;
    scores.to_daily += 3;
    scores.alpha_standard -= 2; scores.alpha_zero -= 3;
    scores.apex_eod -= 3; scores.apex_intraday -= 2;
    scores.lucid_pro -= 2; scores.lucid_direct -= 2;
    scores.to_elite -= 3; scores.to_instant -= 3; scores.to_s2f -= 2;
    scores.to_ignite -= 3; scores.e2t_mini -= 5;
    scores.e8_sig -= 1; scores.fn_bolt -= 2;
  } else if (dll === 'soft_ok') {
    scores.alpha_standard += 3; scores.alpha_zero += 3;
    scores.lucid_pro += 2; scores.lucid_direct += 2;
    scores.apex_eod += 2; scores.apex_intraday += 2;
    scores.to_elite += 2; scores.to_instant += 2; scores.to_ignite += 2;
    scores.tradeify_select += 2; scores.e8_sig += 2; scores.fn_bolt += 2;
    scores.e2t_mini -= 4;
  } else if (dll === 'dont_care') {
    scores.mffu_flex += 0; scores.tradeify_select += 0; scores.alpha_standard += 0;
  }

  // Q10: consistency_pref
  const consistency = answers.consistency_pref;
  if (consistency === 'none') {
    scores.mffu_flex += 6; scores.lucid_flex += 6; scores.ts_standard += 5;
    scores.alpha_advanced += 5; scores.tradeify_select += 4;
    scores.td_eod += 4; scores.td_intraday += 3; scores.td_static += 3;
    scores.tpt_pro += 3; scores.fn_bolt += 2; scores.to_daily += 2;
    scores.to_ignite -= 6; scores.lucid_direct -= 5;
    scores.to_s2f -= 5; scores.to_instant -= 4; scores.to_elite -= 4;
    scores.lucid_pro -= 3; scores.fn_rapid -= 3;
    scores.alpha_standard -= 2; scores.alpha_zero -= 2;
    scores.e8_sig -= 3; scores.ts_consistency -= 2;
  } else if (consistency === 'loose_ok') {
    scores.alpha_standard += 3; scores.alpha_zero += 3;
    scores.ts_consistency += 2; scores.lucid_pro += 3; scores.fn_rapid += 2;
    scores.mffu_flex += 2; scores.lucid_flex += 2;
    scores.e8_sig += 1;
    scores.to_ignite -= 4; scores.lucid_direct -= 3;
    scores.to_s2f -= 3; scores.to_instant -= 2; scores.to_elite -= 2;
  } else if (consistency === 'strict_ok') {
    scores.to_elite += 2; scores.to_instant += 2; scores.lucid_direct += 2;
    scores.e8_sig += 2; scores.to_ignite += 1; scores.tradeify_lightning += 2;
  }

  // Q11: scaling_style
  const scaling = answers.scaling_style;
  if (scaling === '1to2') {
    scores.mffu_pro += 5; scores.alpha_advanced += 4;
    scores.tradeify_select += 3; scores.tpt_pro += 3;
    scores.td_eod += 2; scores.ts_standard += 2;
  } else if (scaling === '3to5') {
    scores.apex_eod += 3; scores.mffu_pro += 2;
    scores.lucid_flex += 2; scores.lucid_pro += 2; scores.tradeify_select += 2;
    scores.alpha_advanced -= 2; scores.alpha_standard -= 2; scores.alpha_zero -= 2;
  } else if (scaling === 'max') {
    scores.apex_eod += 7; scores.apex_intraday += 5;
    scores.to_elite += 4; scores.to_instant += 3;
    scores.to_s2f += 3; scores.to_ignite += 3;
    scores.alpha_advanced -= 5; scores.alpha_standard -= 5; scores.alpha_zero -= 5;
    scores.mffu_pro -= 2; scores.mffu_flex -= 2;
    scores.ts_standard -= 1; scores.e2t_mini -= 4; scores.tpt_pro -= 2;
  }

  // Q12: platform
  const platform = answers.platform;
  const platformSupport = {
    ninjatrader: {
      supported: ['tradeify_select','tradeify_lightning','alpha_standard','alpha_advanced','alpha_zero','mffu_flex','mffu_pro','mffu_rapid','tpt_pro','fn_bolt','fn_rapid','fn_legacy','lucid_flex','lucid_pro','lucid_direct','to_elite','to_daily','to_instant','to_s2f','to_ignite','td_eod','td_intraday','td_static','e2t_mini'],
      dealbreakers: { ts_standard: -8, ts_consistency: -8, e8_sig: -6 }
    },
    tradovate: {
      supported: ['apex_eod','apex_intraday','tradeify_select','tradeify_lightning','alpha_standard','alpha_advanced','alpha_zero','mffu_flex','mffu_pro','mffu_rapid','tpt_pro','fn_bolt','fn_rapid','fn_legacy','lucid_flex','lucid_pro','lucid_direct','to_elite','to_daily','to_instant','to_s2f','to_ignite','td_eod','td_intraday','td_static','e2t_mini','e8_sig'],
      dealbreakers: { ts_standard: -8, ts_consistency: -8 }
    },
    tradingview: {
      supported: ['tradeify_select','tradeify_lightning','alpha_standard','alpha_advanced','alpha_zero','mffu_flex','mffu_pro','mffu_rapid','tpt_pro','fn_bolt','fn_rapid','fn_legacy','lucid_flex','lucid_pro','lucid_direct','to_elite','to_daily','to_instant','to_s2f','to_ignite','td_eod','td_intraday','td_static','e2t_mini','e8_sig'],
      dealbreakers: { ts_standard: -8, ts_consistency: -8, apex_eod: -5, apex_intraday: -5 }
    },
    quantower: {
      // Quantower / ProjectX — Alpha no longer supports ProjectX
      supported: ['tradeify_select','tradeify_lightning','mffu_flex','mffu_pro','mffu_rapid','tpt_pro','lucid_flex','lucid_pro','lucid_direct','to_elite','to_daily','to_instant','to_s2f','to_ignite','td_eod','td_intraday','td_static','e2t_mini'],
      dealbreakers: { ts_standard: -8, ts_consistency: -8, apex_eod: -5, apex_intraday: -5, e8_sig: -5, fn_bolt: -5, fn_rapid: -5, fn_legacy: -5, alpha_standard: -5, alpha_advanced: -5, alpha_zero: -5 }
    },
    rithmic: {
      // Rithmic / TradeSea — firms with Rithmic connectivity
      supported: ['apex_eod','apex_intraday','tradeify_select','tradeify_lightning','to_elite','to_daily','to_instant','to_s2f','to_ignite','lucid_flex','lucid_pro','lucid_direct'],
      dealbreakers: { ts_standard: -8, ts_consistency: -8, mffu_flex: -5, mffu_pro: -5, mffu_rapid: -5, alpha_standard: -5, alpha_advanced: -5, alpha_zero: -5, fn_bolt: -5, fn_rapid: -5, fn_legacy: -5, e8_sig: -5, e2t_mini: -5, td_eod: -5, td_intraday: -5, td_static: -5, tpt_pro: -5 }
    },
  };

  if (platform !== 'any' && platformSupport[platform]) {
    const ps = platformSupport[platform];
    ps.supported.forEach(id => { scores[id] += 3; });
    Object.entries(ps.dealbreakers).forEach(([id, penalty]) => { scores[id] += penalty; });
  } else if (platform === 'any') {
    scores.lucid_flex += 3; scores.lucid_pro += 3; scores.lucid_direct += 3;
    scores.tpt_pro += 2;
    scores.e2t_mini += 1;
  }

  return ids
    .map(id => ({ ...ACCOUNT_TYPES[id], score: scores[id] }))
    .sort((a, b) => b.score - a.score)
    .map(acct => ({ ...acct, reasons: generateReasons(acct, answers) }));
}

function deduplicateResults(sorted) {
  const result = [];
  const firmSeen = {};
  for (const acct of sorted) {
    if (result.length >= 3) break;
    if (firmSeen[acct.firmId] !== undefined) {
      const existingScore = firmSeen[acct.firmId];
      if (existingScore - acct.score <= 2) continue;
    }
    result.push(acct);
    firmSeen[acct.firmId] = acct.score;
  }
  if (result.length < 3) {
    for (const acct of sorted) {
      if (result.length >= 3) break;
      if (!result.find(r => r.id === acct.id)) result.push(acct);
    }
  }
  return result;
}

function generateReasons(acct, answers) {
  const reasons = [];

  // --- Budget ---
  if (answers.budget === 'under50') {
    if (acct.id === 'apex_eod' || acct.id === 'apex_intraday') reasons.push('Cheapest evals in the industry — as low as ~$24 with promos. Great for learning without big risk.');
    if (acct.id === 'mffu_flex') reasons.push('$0 activation fee — all-in cost as low as ~$49 for a 25K account.');
  }
  if (answers.budget === '50to150') {
    if (!acct.activationFee && reasons.length < 3) reasons.push('No activation fee — the eval price is your total cost to get funded.');
  }

  // --- Challenge ---
  if (answers.challenge === 'passing') {
    if (acct.id === 'apex_eod' || acct.id === 'apex_intraday') reasons.push("One-time eval fee with no time pressure on 4.0 — cheap to retry if you don't pass.");
    if (acct.fundedConsistency === 'none' && acct.hasEval && reasons.length < 3) reasons.push('No eval consistency rule — easier path to passing.');
  }
  if (answers.challenge === 'staying_funded') {
    if (acct.fundedConsistency === 'none' && !acct.hasDLL && reasons.length < 3) reasons.push('No funded consistency rule and no DLL — the most forgiving funded setup available.');
    if (acct.dllType === 'soft (DLG)' && reasons.length < 3) reasons.push('Daily Loss Guard protects you from blowup days without failing your account.');
  }
  if (answers.challenge === 'consistent_payouts') {
    if (acct.fundedConsistency === 'none' && reasons.length < 3) reasons.push("No consistency rule on funded — withdraw whenever you're profitable without hitting caps.");
  }
  if (answers.challenge === 'maximizing') {
    if (acct.maxPayout === 'No cap' && reasons.length < 3) reasons.push('No per-payout cap — withdraw any amount above the buffer.');
    if (acct.profitSplit === '100%' && reasons.length < 3) reasons.push('100% profit split — keep every dollar you earn.');
  }

  // --- Eval preference ---
  if (answers.eval_pref === 'skip' && acct.isInstantFunded) {
    reasons.push('Instant funded — no evaluation required. Start trading immediately.');
  }

  // --- Trading style ---
  if (answers.style === 'scalper') {
    if (acct.fundedConsistency === 'none' && reasons.length < 3) reasons.push('No funded consistency rule — ideal for high-frequency scalpers who have big days.');
  }
  if (answers.style === 'swing' && acct.overnightAllowed) {
    reasons.push('Overnight holds allowed — one of the only prop firms that supports swing trading.');
  }

  // --- Drawdown ---
  if (answers.drawdown_pref === 'eod' && acct.drawdown === 'eod' && reasons.length < 3) {
    if (acct.id.startsWith('ts_')) reasons.push('Pure EOD trailing — your drawdown floor only moves at market close, never intraday.');
  }
  if (answers.drawdown_pref === 'intraday' && acct.drawdown === 'intraday' && reasons.length < 3) {
    reasons.push('Intraday trailing drawdown — matches your preference for real-time tracking.');
  }

  // --- DLL ---
  if (answers.dll_pref === 'no_dll' && !acct.hasDLL && reasons.length < 3) {
    reasons.push('No Daily Loss Limit — the trailing drawdown is your only floor.');
  }
  if (answers.dll_pref === 'soft_ok' && acct.dllType && acct.dllType.includes('soft') && reasons.length < 3) {
    reasons.push('Soft DLL pauses trading on bad days without failing your account — built-in risk protection.');
  }

  // --- Consistency ---
  if (answers.consistency_pref === 'none' && acct.fundedConsistency === 'none' && reasons.length < 3) {
    reasons.push('No funded consistency rule — size heavy early, build a buffer, then cycle winning days.');
  }

  // --- Scaling ---
  if (answers.scaling_style === 'max') {
    if (acct.maxAccounts >= 20 && reasons.length < 3) reasons.push(`Up to ${acct.maxAccounts} funded accounts simultaneously — built for mass scaling.`);
    if (acct.maxAccounts >= 10 && acct.maxAccounts < 20 && reasons.length < 3) reasons.push(`Up to ${acct.maxAccounts} accounts — strong scaling potential.`);
  }
  if (answers.scaling_style === '1to2') {
    if (acct.maxPayout === 'No cap' && reasons.length < 3) reasons.push('No payout cap — maximum earnings from a single account.');
    if (acct.maxPayout && acct.maxPayout.includes('$15,000') && reasons.length < 3) reasons.push('$15,000 max payout per request — most per-account earning potential.');
  }

  // --- Platform ---
  if (answers.platform === 'any' && acct.platforms.length >= 8 && reasons.length < 3) {
    reasons.push(`Widest platform selection — ${acct.platforms.length}+ options including ${acct.platforms.slice(0, 3).join(', ')}, and more.`);
  }

  // --- News restriction note (always add if applicable and account is being shown) ---
  if (acct.newsRestricted && acct.newsNote && reasons.length < 4) {
    reasons.push(`Note: ${acct.newsNote}`);
  }

  // --- Fill remaining with strengths if needed ---
  if (reasons.length < 2) {
    for (const strength of acct.strengths) {
      if (!reasons.includes(strength) && reasons.length < 3) {
        reasons.push(strength);
      }
    }
  }

  return reasons.slice(0, 4);
}

function Quiz({ onViewFirm }) {
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;
  const question = QUESTIONS[currentQuestion];
  const isLastQuestion = currentQuestion === QUESTIONS.length - 1;

  const handleSelect = (value) => {
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);
    if (isLastQuestion) {
      const scored = scoreResults(newAnswers);
      setResults(deduplicateResults(scored));
      window.scrollTo(0, 0);
    } else {
      setCurrentQuestion(currentQuestion + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const handleRestart = () => {
    setStarted(false);
    setCurrentQuestion(0);
    setAnswers({});
    setResults(null);
    window.scrollTo(0, 0);
  };

  // === INTRO SCREEN ===
  if (!started) {
    return (
      <div className={styles.quizPage}>
        <div className={styles.quizContainer}>
          <div className={styles.introBlock}>
            <span className={styles.introBadge}>12 Questions</span>
            <h1 className={styles.introTitle}>Find Your Best Account Type</h1>
            <p className={styles.introSubtitle}>
              Answer a few questions about your experience, trading style, and priorities. We'll score all 29 account types across 12 firms and show you the top 3 matches — down to the specific product, not just the firm.
            </p>
            <ul className={styles.introFeatures}>
              <li>Takes about 2 minutes</li>
              <li>Recommends specific account types, not just firms</li>
              <li>Results link directly to each firm's full detail page</li>
            </ul>
            <button
              type="button"
              className={styles.startButton}
              onClick={() => { setStarted(true); window.scrollTo(0, 0); }}
            >
              Take the Quiz →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (results) {
    const isNewTrader = answers.payout_count === 'zero' || answers.payout_count === '1to3'
      || answers.challenge === 'passing' || answers.challenge === 'staying_funded';

    return (
      <div className={styles.quizPage}>
        <div className={styles.quizContainer}>
          <h1 className={styles.resultsTitle}>Your Top Accounts</h1>
          <p className={styles.resultsSubtitle}>
            Based on your answers, these account types are the best fit for where you are right now.
          </p>
          <div className={styles.resultsGrid}>
            {results.map((acct, i) => (
              <div key={acct.id} className={`${styles.resultCard} ${i === 0 ? styles.resultCardTop : ''}`} onClick={() => onViewFirm(acct.firmId)}>
                <span className={styles.resultRank}>#{i + 1} Match</span>
                <h2 className={styles.resultFirmName}>{acct.firmName} · {acct.productName}</h2>
                <p className={styles.resultWhyTitle}>Why this account?</p>
                <div className={styles.resultReasons}>
                  {acct.reasons.map((reason, j) => (
                    <p key={j} className={styles.resultReason}>{reason}</p>
                  ))}
                </div>
                <span className={styles.resultViewLink}>View full details →</span>
              </div>
            ))}
          </div>
          {isNewTrader && (
            <p className={styles.resultsDisclaimer}>
              These recommendations are designed to help you level up. Build consistency here — once you're taking regular payouts, you'll be ready for accounts with higher splits, bigger payout caps, and paths to live capital.
            </p>
          )}
          {!isNewTrader && (
            <p className={styles.resultsDisclaimer}>
              These recommendations match your experience level and trading style. Every firm on PropFirmAtlas offers a legitimate opportunity — the best account for you depends on your specific goals and preferences.
            </p>
          )}
          <button type="button" className={styles.restartButton} onClick={handleRestart}>
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.quizPage}>
      <div className={styles.quizContainer}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
        <div className={styles.progressLabel}>
          Question {currentQuestion + 1} of {QUESTIONS.length}
          <span className={styles.sectionLabel}> — {question.section}</span>
        </div>

        <h2 className={styles.questionText}>{question.text}</h2>
        {question.note && <p className={styles.questionNote}>{question.note}</p>}

        <div className={styles.optionsGrid}>
          {question.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.optionButton} ${answers[question.id] === opt.value ? styles.optionSelected : ''}`}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {currentQuestion > 0 && (
          <button type="button" className={styles.backButton} onClick={handleBack}>
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}

export default Quiz;
